import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	peoplePageSchema,
	personContractPath,
	personContractSchema,
	personContractsPath,
	personPath,
	personSchema,
	rolesAndTeamsSchema,
	type AddPersonRequest,
	type PeopleQuery,
	type Person,
	type PersonContract,
	type PersonContractRequest,
	type UpdatePersonRequest,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// People & HR's requests. Every query key starts with "people", so adding someone can refresh the list and the roles
// and teams at once.

/**
 * A page of the organization's people, A→Z by name: the first page unless `page` holds a link from another page, and
 * only the people of one team or one role, of one status, or whose name holds `q`, when the query says so. Only members
 * with access to People & HR may read it; the API answers everyone else with 403 and a page link it did not make with
 * 400.
 */
export const peoplePageQueryOptions = ({ page, team, role, status, q }: PeopleQuery = {}) =>
	queryOptions({
		queryKey: ["people", "page", { page, team, role, status, q }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.people, peoplePageSchema, {
				query: {
					...(page !== undefined && { page }),
					...(team !== undefined && { team }),
					...(role !== undefined && { role }),
					...(status !== undefined && { status }),
					...(q !== undefined && { q }),
				},
				signal,
			}),
		// A list a few seconds old is asked again when the page is opened again or the window is come back to; somebody
		// else may have added or retired someone since.
		staleTime: LIST_STALE_TIME_MS,
		// A refused page link or a refused member will not change on a retry, and the page redirects on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/** The roles and the teams the organization has already given its people, which the form offers. */
export const rolesAndTeamsQueryOptions = () =>
	queryOptions({
		queryKey: ["people", "roles-and-teams"],
		queryFn: ({ signal }) => apiRequest(apiPaths.peopleRolesAndTeams, rolesAndTeamsSchema, { signal }),
	});

/**
 * Adds a person, and keeps their role and team among the organization's. Fails with 400 when the form's values are
 * refused and 403 when the signed-in member is not an editor of People & HR.
 */
export function addPerson(request: AddPersonRequest): Promise<Person> {
	return apiRequest(apiPaths.people, personSchema, { method: "POST", body: request });
}

/**
 * One person of the organization, by id: what their page opens with, including their note. Only members with access to
 * People & HR may read it; the API answers everyone else with 403 and a person who is gone with 404.
 */
export const personQueryOptions = (personId: string) =>
	queryOptions({
		queryKey: ["people", "person", personId],
		queryFn: ({ signal }) => apiRequest(personPath(personId), personSchema, { signal }),
		// A refused member or a person who is gone will not come back on a retry, and the page acts on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * Saves a person's whole record, their note included. Fails with 400 when the values are refused, 403 when the
 * signed-in member is not an editor of People & HR, and 404 when the person is gone.
 */
export function updatePerson(personId: string, request: UpdatePersonRequest): Promise<Person> {
	return apiRequest(personPath(personId), personSchema, { method: "PATCH", body: request });
}

/** Removes a person for good; the audit log keeps what became of them. Fails with 403 and 404 as above. */
export async function removePerson(personId: string): Promise<void> {
	await apiRequest(personPath(personId), z.undefined(), { method: "DELETE" });
}

/**
 * Adds a contract to a person, which then comes back with the person. Fails with 400 when the terms are refused, 403
 * when the signed-in member is not an editor of People & HR, 404 when the person is gone and 409 when they already
 * have as many contracts as a person keeps.
 */
export function addPersonContract(personId: string, request: PersonContractRequest): Promise<PersonContract> {
	return apiRequest(personContractsPath(personId), personContractSchema, { method: "POST", body: request });
}

/** Saves every term of one of a person's contracts. Fails with 400, 403 and 404 as above. */
export function updatePersonContract(
	personId: string,
	contractId: string,
	request: PersonContractRequest,
): Promise<PersonContract> {
	return apiRequest(personContractPath(personId, contractId), personContractSchema, {
		method: "PATCH",
		body: request,
	});
}

/** Takes one contract off a person's record for good; the audit log keeps that it happened. */
export async function removePersonContract(personId: string, contractId: string): Promise<void> {
	await apiRequest(personContractPath(personId, contractId), z.undefined(), { method: "DELETE" });
}
